import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterSelect from '../../../shared/FilterSelect';
import { Button } from '@mui/material';
import { useGetCollegeName } from 'src/hooks/collegeName.hooks';
import CollegeSelect from 'src/components/shared/CollegeSelect';
import { Badge, Label } from 'flowbite-react';
import { debounce } from 'lodash';

const CampusInput = ({ showSelect = false }: { showSelect?: boolean }) => {
	const limit = 10;
	const [search, setSearch] = useState('');
	const [searchParams, setSearchParams] = useSearchParams();
	const campus = JSON.parse(searchParams.get('campus') || '[]');
	const [collegeNames, setCollegeNames] = useState<{ label: string; value: string }[]>([]);
	const [page, setPage] = useState(1);
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const popoverContentRef = useRef<HTMLDivElement>(null);
	const lastSearchRef = useRef('');

	const { data, isLoading } = useGetCollegeName({
		page,
		limit,
		search: search,
		onComplete: data => {
			const newColleges = data.collegeNames.map(c => ({
				label: c.name,
				value: c._id,
			}));

			setCollegeNames(prevColleges => {
				if (search !== lastSearchRef.current || page === 1) {
					lastSearchRef.current = search;
					return newColleges;
				} else {
					const uniqueColleges = [...prevColleges, ...newColleges].reduce((acc: any[], current: any) => {
						const x = acc.find(item => item.value === current.value);
						if (!x) {
							return acc.concat([current]);
						} else {
							return acc;
						}
					}, []);
					return uniqueColleges;
				}
			});
		},
	});

	const handlePopoverClose = () => {
		setIsPopoverOpen(false);
	};

	const debouncedSearchChangeHandler = useCallback(
		debounce((value: string) => {
			setSearch(value);
			setPage(1);
		}, 300),
		[],
	);

	const handleScrollToBottom = () => {
		if (page < (data?.total || 0) / limit) {
			setPage(prevPage => prevPage + 1);
		}
	};

	return (
		<div className="w-full whiteBgSelect">
			{showSelect ? <Label className="mb-1 inline-block">Campus</Label> : null}
			{showSelect ? (
				<CollegeSelect
					isMulti={showSelect}
					placeholder="Campus"
					className="flowbite-select--md"
					styleType={showSelect ? 'flowbite-select' : 'my-select-plans'}
					onChange={(value: { value: string; label: string } | { value: string; label: string }[]) => {
						if (Array.isArray(value)) {
							const campusValues = value.map(item => item.value);
							searchParams.set('campus', JSON.stringify(campusValues));
							setSearchParams(searchParams);
						} else if (value?.value) {
							searchParams.set('campus', JSON.stringify([...campus, value.value]));
							setSearchParams(searchParams);
						}
					}}
				/>
			) : (
				<FilterSelect
					arrow={false}
					isLoading={isLoading}
					active={!!campus?.length}
					placeholder={'Campus'}
					onClose={handlePopoverClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					maxHeight="300px"
					maxWidth="300px"
					showSearch
					onScrollToBottom={handleScrollToBottom}
					onSearchChange={e => {
						debouncedSearchChangeHandler(e?.target?.value);
					}}
				>
					{({ handlePopoverClose }) => (
						<div ref={popoverContentRef} className="min-w-[255px] bg-white dark:bg-[#1F2A37] max-h-[300px]">
							{!collegeNames?.length ? (
								<div className="text-center py-3 text-gray-500 text-sm">No college found</div>
							) : null}
							{collegeNames.map(option => (
								<Button
									key={option?.value}
									sx={{
										textAlign: 'left',
										display: 'block',
										py: '8px',
										width: '100%',
										px: '18px',
										fontSize: '14px',
										fontWeight: '400',
										color: 'inherit',
										borderRadius: '0',
										textTransform: 'none',
										whiteSpace: 'break-spaces',
										'&:hover': { backgroundColor: '#7777e73f' },
										backgroundColor: campus?.includes(option?.value?.toString()) ? '#7777e71b' : 'transparent',
									}}
									onClick={() => {
										if (campus?.includes(option?.value?.toString())) {
											searchParams.set(
												'campus',
												JSON.stringify(campus?.filter((skill: string) => skill !== option?.value?.toString())),
											);
											setSearchParams(searchParams);
											return;
										}
										searchParams.set('campus', JSON.stringify([...campus, option?.value?.toString()]));
										setSearchParams(searchParams);
									}}
								>
									{option?.label}
									{campus?.includes(option?.value?.toString()) ? (
										<Badge className="rounded-[6px] font-[500] text-xs inline-flex w-fit ml-1" size="xs" color="dark">
											Selected
										</Badge>
									) : null}
								</Button>
							))}
							{isLoading && <div className="text-center py-2">Loading...</div>}
						</div>
					)}
				</FilterSelect>
			)}
		</div>
	);
};

export default CampusInput;
